/**
 * Every path the application can be at, declared once.
 *
 * Nothing in the client writes a path literal. A link is built from a builder
 * here, so renaming a route is one edit and a typo is a type error.
 */

export const ROUTES = {
  // Public
  landing: () => '/',
  news: () => '/news',
  newsArticle: (slug: string) => `/news/${slug}`,
  ads: () => '/ads',
  myAds: () => '/ads/mine',
  adNew: () => '/ads/new',
  adEdit: (id: string) => `/ads/${id}/edit`,
  links: () => '/links',
  /** راهنمای معامله، قوانین، درباره ما، تماس: the operator edited pages. */
  contentPage: (slug: string) => `/pages/${slug}`,
  market: () => '/market',
  marketPrices: () => '/market/prices',
  marketLme: () => '/market/lme',
  marketFx: () => '/market/fx',
  marketDashboard: () => '/market/dashboard',
  marketPriceIndex: () => '/market/price-index',
  marketHistory: () => '/market/history',
  marketArchive: () => '/market/archive',

  // Entry and onboarding
  login: (next?: string) => (next ? `/login?next=${encodeURIComponent(next)}` : '/login'),
  register: () => '/register',
  registerStep: (step: OnboardingStepSlug) => `/register/${step}`,

  // Member
  dashboard: () => '/dashboard',
  board: () => '/board',
  boardFiltered: (q: Record<string, string>) => withQuery('/board', q),
  rfqBoard: () => '/board/rfq',
  dealsBoard: () => '/board/deals',

  offerNew: () => '/offers/new',
  offerNewStep: (step: OfferWizardStep) => `/offers/new/${step}`,
  offerNewBrand: () => '/offers/new/product/brand',
  offerDetail: (offerId: number | string) => `/offers/${offerId}`,
  offerEdit: (offerId: number | string) => `/offers/${offerId}/edit`,
  offerCancel: (offerId: number | string) => `/offers/${offerId}/cancel`,
  offerBid: (offerId: number | string) => `/offers/${offerId}/bid`,
  myOffers: () => '/offers/mine',

  rfqNew: () => '/rfqs/new',
  rfqNewStep: (step: RfqWizardStep | RfqWizardLegacyStep) => `/rfqs/new/${step}`,
  rfqDetail: (rfqId: number | string) => `/rfqs/${rfqId}`,
  rfqBid: (rfqId: number | string) => `/rfqs/${rfqId}/bid`,
  rfqEdit: (rfqId: number | string) => `/rfqs/${rfqId}/edit`,
  rfqCancel: (rfqId: number | string) => `/rfqs/${rfqId}/cancel`,
  myRfqs: () => '/rfqs/mine',

  bids: () => '/bids',
  bidHistory: (bidId: number | string) => `/bids/${bidId}/history`,

  negotiation: (negotiationId: number | string) => `/negotiations/${negotiationId}`,
  deals: () => '/deals',
  dealRoom: (dealId: number | string) => `/deals/${dealId}`,
  dealNotice: (dealId: number | string, side?: 'buyer' | 'seller') =>
    side ? `/deals/${dealId}/notice?side=${side}` : `/deals/${dealId}/notice`,
  dealDocuments: (dealId: number | string) => `/deals/${dealId}/documents`,
  dealDelivery: (dealId: number | string) => `/deals/${dealId}/delivery`,

  documents: () => '/documents',
  notifications: () => '/notifications',
  favorites: () => '/favorites',
  requests: () => '/requests',
  tickets: () => '/tickets',
  ticket: (ticketNo: number | string) => `/tickets/${ticketNo}`,
  wallet: () => '/wallet',
  walletTransactions: () => '/wallet/transactions',
  grade: () => '/account/grade',
  claims: () => '/claims',
  claimNew: (dealId?: number | string) =>
    dealId === undefined ? '/claims/new' : `/claims/new?deal=${dealId}`,
  claim: (claimNo: number | string) => `/claims/${claimNo}`,

  settings: () => '/settings',
  settingsProfile: () => '/settings/profile',
  settingsUsers: () => '/settings/users',
  settingsAddresses: () => '/settings/addresses',
  settingsKyc: () => '/settings/kyc',
  settingsNotifications: () => '/settings/notifications',
  settingsSecurity: () => '/settings/security',

  // Operator console. A separate app, served under /admin, with its own sign
  // in and its own error pages. An item a desk lists opens at its own
  // address under the desk: /admin/kyc/2317, /admin/offers/1056.
  admin: () => '/admin',
  adminLogin: (next?: string) =>
    next ? `/admin/login?next=${encodeURIComponent(next)}` : '/admin/login',
  adminForbidden: () => '/admin/403',
  /** The match desk is a workspace, not a list of pages: the offer stays in the query. */
  adminMatching: (offerId?: number | string) =>
    offerId ? `/admin/matching?offer=${offerId}` : '/admin/matching',
  adminOffers: (offerId?: number | string) => adminItem('/admin/offers', offerId),
  adminRfqs: (rfqId?: number | string) => adminItem('/admin/rfqs', rfqId),
  adminKyc: (fileId?: number | string) => adminItem('/admin/kyc', fileId),
  adminBrands: (brandId?: number | string) => adminItem('/admin/brands', brandId),
  adminClaims: (claimId?: number | string) => adminItem('/admin/claims', claimId),
  adminTransfers: (depositId?: number | string) => adminItem('/admin/transfers', depositId),
  adminTickets: (ticketNo?: number | string) => adminItem('/admin/tickets', ticketNo),
  adminUsers: (userId?: number | string) => adminItem('/admin/users', userId),
  adminRoles: () => '/admin/roles',
  adminDeals: (dealId?: number | string) => adminItem('/admin/deals', dealId),
  adminPrices: () => '/admin/prices',
  adminNews: () => '/admin/news',
  adminNewsWrite: () => '/admin/news/write',
  adminNewsEdit: (id: string) => `/admin/news/${id}/edit`,
  adminContent: () => '/admin/content',
  adminAdvertisers: (formId?: string) => adminItem('/admin/advertisers', formId),
  adminReports: () => '/admin/reports',
  adminAudit: () => '/admin/audit',
  adminConfig: () => '/admin/config',

  // Errors
  forbidden: () => '/403',
  notFound: () => '/404',
} as const

/**
 * The complete-profile flow, the screens a member walks to unlock trading.
 * `account` is drawn only for a visitor who is not signed in yet, and `done` is
 * the confirmation after the file is sent. The server says which of the rest
 * an account stands on.
 */
export type OnboardingStepSlug =
  | 'account'
  | 'type'
  | 'profile'
  | 'contract'
  | 'done'
  | 'pending'
  | 'rejected'

/** What `GET /onboarding/me` answers with. `active` is the dashboard, not a screen. */
export type OnboardingStage = 'type' | 'profile' | 'contract' | 'pending' | 'rejected' | 'active'

/**
 * The sale form: six steps, then the receipt. `deal` carries the product, the
 * brand and the four figures the sale is decided on; the others group what
 * belongs together (spec, weight, price and payment, delivery and validity).
 */
export type OfferWizardStep =
  | 'deal'
  | 'spec'
  | 'weight'
  | 'payment'
  | 'delivery'
  | 'review'
  | 'submitted'
/** The slugs of the earlier layout, still found in saved links and notifications. */
export type LegacyOfferWizardStep = 'product' | 'terms'
/**
 * «ثبت تقاضای خرید», the sale form's six steps under the same names, then
 * the receipt. `submitted` is the receipt and stands outside the rail.
 */
export type RfqWizardStep =
  | 'deal'
  | 'spec'
  | 'weight'
  | 'payment'
  | 'delivery'
  | 'review'
  | 'submitted'

/** Slugs of the old four step demand wizard; a link to one opens the step that holds its fields. */
export type RfqWizardLegacyStep = 'product' | 'terms'

export const OFFER_WIZARD_STEPS: Array<{ slug: OfferWizardStep; label: string }> = [
  { slug: 'deal', label: 'مشخصات معامله' },
  { slug: 'spec', label: 'مشخصات فنی' },
  { slug: 'weight', label: 'شرایط وزنی' },
  { slug: 'payment', label: 'قیمت و پرداخت' },
  { slug: 'delivery', label: 'تحویل و اعتبار' },
  { slug: 'review', label: 'بررسی و تأیید' },
  { slug: 'submitted', label: 'ارسال شد' },
]

/**
 * Where a link written for the earlier layout lands: each old slug opens the
 * new step that now holds its first fields.
 */
export const OFFER_WIZARD_LEGACY_STEPS: Record<LegacyOfferWizardStep, OfferWizardStep> = {
  product: 'deal',
  terms: 'deal',
}

export const RFQ_WIZARD_STEPS: Array<{ slug: Exclude<RfqWizardStep, 'submitted'>; label: string }> = [
  { slug: 'deal', label: 'مشخصات معامله' },
  { slug: 'spec', label: 'مشخصات فنی' },
  { slug: 'weight', label: 'شرایط وزنی' },
  { slug: 'payment', label: 'قیمت و پرداخت' },
  { slug: 'delivery', label: 'تحویل و اعتبار' },
  { slug: 'review', label: 'بررسی و تأیید' },
]

/** Where an old demand wizard link lands: the step that now holds its first field. */
export const RFQ_WIZARD_LEGACY_STEPS: Record<RfqWizardLegacyStep, RfqWizardStep> = {
  product: 'deal',
  terms: 'deal',
}

export const ONBOARDING_PHASES = ['نوع شخص', 'اطلاعات', 'قرارداد'] as const

/** The bullet each screen lights, one based. */
export const ONBOARDING_PHASE_OF: Record<OnboardingStepSlug, number> = {
  account: 1,
  type: 1,
  profile: 2,
  contract: 3,
  done: 3,
  pending: 3,
  rejected: 2,
}

/** A desk's list, or one of its items at its own address under it. */
function adminItem(list: string, id: number | string | undefined): string {
  return id === undefined || id === '' ? list : `${list}/${encodeURIComponent(String(id))}`
}

function withQuery(path: string, params: Record<string, string | number | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '')
  if (!entries.length) return path
  const qs = entries.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&')
  return `${path}?${qs}`
}

export { withQuery }
